import { landmineNames } from '@mm/constants/landmine-names';
import {
  CampaignAcceptableTime,
  CampaignWeekdayOrdinal,
} from '@mm/state/model/CampaignsModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { isEmpty, pickBy } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Input, Label } from 'reactstrap';
import Button from '../../buttons/DeprecatedButton/DeprecatedButton';
import { UNREACHABLE_MESSAGE } from '../../constants/promotionalMessages';
import {
  END_OF_WHOLE_DAY,
  EVERY_DAY,
  formatAcceptableTime,
  START_OF_WHOLE_DAY,
} from '../../helpers/date-formatting';
import ErrorAlert from '../ErrorAlert/ErrorAlert';
import { ExpandableWidgetContainer } from '../ExpandableWidgetContainer';
import { TimePicker } from '../TimePicker/TimePicker';
import './AcceptableTimeBuilder.css';

const DEFAULT_START_TIME = '10:00';
const DEFAULT_STOP_TIME = '18:00';

type Props = {
  className?: string;
  acceptableTime: CampaignAcceptableTime;
  onChangeAcceptableTime: (acceptableTime: CampaignAcceptableTime) => void;
  allowNextDayStopTime: boolean;
  isPromotional: boolean;
  isFacebookPlatform: boolean;
};

export const AcceptableTimeBuilder = (props: Props) => {
  const {
    className,
    acceptableTime,
    onChangeAcceptableTime,
    allowNextDayStopTime,
    isPromotional,
    isFacebookPlatform,
  } = props;
  const modalActions = useModalActions();
  const { daysOfWeek, startTime, stopTime } = acceptableTime;

  const [startTimeRef, setStartTimeRef] = useState<HTMLDivElement | undefined>(
    undefined,
  );
  const [stopTimeRef, setStopTimeRef] = useState<HTMLDivElement | undefined>(
    undefined,
  );
  const extraInnerRefs = [startTimeRef, stopTimeRef].filter(
    r => !!r,
  ) as HTMLDivElement[];

  const effectiveDaysOfWeek = daysOfWeek || EVERY_DAY;
  const effectiveStartTime = startTime || START_OF_WHOLE_DAY;
  const effectiveStopTime = stopTime || END_OF_WHOLE_DAY;

  const wholeDay =
    effectiveStartTime === START_OF_WHOLE_DAY &&
    effectiveStopTime === END_OF_WHOLE_DAY;

  const canUseAcceptableTimebuilder = useSelector(
    combinedFeatureFlagsSelector,
  ).automationDripCampaignsAcceptableTimebuilder;

  const amendAcceptableTime = (
    acceptableTimeProps: Partial<CampaignAcceptableTime>,
  ) => {
    if (!canUseAcceptableTimebuilder) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName:
          landmineNames[
            'MARKETING-AUTOMATION__DRIP-CAMPAIGNS__ACCEPTABLE-TIMEBUILDER'
          ],
        feature: 'automationDripCampaignsAcceptableTimebuilder',
      });
    } else {
      let updatedValue = pickBy({ ...acceptableTime, ...acceptableTimeProps });
      if (updatedValue.startTime || updatedValue.stopTime) {
        // Make sure we always have a daysOfWeek selection with start/stop times
        updatedValue = {
          daysOfWeek: effectiveDaysOfWeek,
          startTime: effectiveStartTime,
          stopTime: effectiveStopTime,
          ...updatedValue,
        };
      } else if (
        updatedValue.daysOfWeek &&
        updatedValue.daysOfWeek.length === 7
      ) {
        // Erase daysOfWeek if we chose all days and don't have start/stop time
        delete updatedValue.daysOfWeek;
      }
      onChangeAcceptableTime(updatedValue);
    }
  };

  const onToggleDay = (dayToToggle: CampaignWeekdayOrdinal) => {
    let newDaysOfWeek: CampaignWeekdayOrdinal[];

    if (effectiveDaysOfWeek.indexOf(dayToToggle) > -1) {
      newDaysOfWeek = effectiveDaysOfWeek.filter(i => i !== dayToToggle);
    } else {
      newDaysOfWeek = [...effectiveDaysOfWeek, dayToToggle].sort(
        (a, b) => a - b,
      );
    }

    amendAcceptableTime({ daysOfWeek: newDaysOfWeek });
  };

  const onUseTimeRange = () => {
    if (wholeDay) {
      amendAcceptableTime({
        daysOfWeek: effectiveDaysOfWeek,
        startTime: DEFAULT_START_TIME,
        stopTime: DEFAULT_STOP_TIME,
      });
    }
  };

  const daysOfWeekSet = new Set(effectiveDaysOfWeek);
  const isScheduled =
    !isEmpty(acceptableTime) &&
    acceptableTime.daysOfWeek &&
    (acceptableTime.daysOfWeek.length !== 0 ||
      acceptableTime.startTime ||
      acceptableTime.stopTime);

  return (
    <>
      <ExpandableWidgetContainer extraInsideElements={extraInnerRefs}>
        {(expanded, setExpanded) => (
          <div className={`acceptable-time-builder ${className || ''}`}>
            <div
              className="acceptable-time-builder__heading"
              onClick={() => setExpanded(!expanded)}
            >
              <div className="acceptable-time-builder__heading-text">
                {formatAcceptableTime(acceptableTime, allowNextDayStopTime)}
              </div>
              <div className="acceptable-time-builder__toggle">
                <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} />
              </div>
            </div>
            {expanded && (
              <div className="acceptable-time-builder__expanded-area">
                <div className="acceptable-time-builder__day-selector">
                  Days
                  <div className="acceptable-time-builder__day-buttons">
                    {EVERY_DAY.map(i => (
                      <Button
                        className="acceptable-time-builder__day-button"
                        blue={daysOfWeekSet.has(i)}
                        gray={!daysOfWeekSet.has(i)}
                        key={i}
                        onClick={() => onToggleDay(i)}
                      >
                        {moment().day(i).format('dd')}
                      </Button>
                    ))}
                  </div>
                  <div className="acceptable-time-builder__day-action-buttons">
                    <Button
                      className="AcceptableTimer-builder__every-day"
                      link
                      onClick={() =>
                        amendAcceptableTime({ daysOfWeek: EVERY_DAY })
                      }
                    >
                      Every day
                    </Button>
                    <Button
                      className="AcceptableTimer-builder__every-day"
                      link
                      onClick={() => amendAcceptableTime({ daysOfWeek: [] })}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                <div className="acceptable-time-builder__time-selector">
                  <Label
                    className={`acceptable-time-builder__radio-label${
                      wholeDay ? '' : '--disabled'
                    }`}
                  >
                    <Input
                      className="acceptable-time-builder__radio"
                      type="radio"
                      checked={wholeDay}
                      onChange={() =>
                        amendAcceptableTime({
                          startTime: undefined,
                          stopTime: undefined,
                        })
                      }
                    />
                    Whole Day
                  </Label>
                  <div className="acceptable-time-builder__custom-time">
                    <Input
                      className="acceptable-time-builder__radio"
                      type="radio"
                      checked={!wholeDay}
                      onChange={onUseTimeRange}
                    />
                    <div className="acceptable-time-builder__time-picker-block">
                      <div className="acceptable-time-builder__time-picker-label">
                        From
                      </div>
                      <TimePicker
                        value={effectiveStartTime}
                        disabled={wholeDay}
                        onChange={startTime =>
                          amendAcceptableTime({ startTime })
                        }
                        calendarRef={setStartTimeRef}
                      />
                    </div>
                    <div className="acceptable-time-builder__time-picker-block">
                      <div className="acceptable-time-builder__time-picker-label">
                        To
                      </div>
                      <TimePicker
                        value={effectiveStopTime}
                        disabled={wholeDay}
                        onChange={stopTime => amendAcceptableTime({ stopTime })}
                        calendarRef={setStopTimeRef}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </ExpandableWidgetContainer>

      {isScheduled && isPromotional && isFacebookPlatform && (
        <div className="acceptable-time-builder__alert">
          <ErrorAlert message={UNREACHABLE_MESSAGE} />
        </div>
      )}
    </>
  );
};
